/**
 * 파일명 : usr0101m.jsx
 * 설명 : 휴가현황화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-21   방진선     직원명 콤보로 수정 및 엔터키 조회    
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0101mAction } from '../../../redux/slice/usr/usr0101m_slice';
import { IOffrDcdItemState, IUserItemState, IUserOffHisState, IInquiryCndsState } from '../../../redux/models/usr/usr0101m_model';

/*  AntD  */
import { Table, Button, Space, Select, Row, Col, Layout, Typography, Collapse, DatePicker, Descriptions, ConfigProvider} from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

import dayjs from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/

const Usr0101m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const inquiryCnds   = useAppSelector((state) => state.Usr0101m.inquiryCnds);
    const userList      = useAppSelector((state) => state.Usr0101m.userList);
    const offrDcdList   = useAppSelector((state) => state.Usr0101m.offrDcdList);
    const userOffHis    = useAppSelector((state) => state.Usr0101m.userOffHis);
    const userOffStatus = useAppSelector((state) => state.Usr0101m.userOffStatus);
    
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        setOffrDcdList();
        fn_User();
        fn_Search();
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content }     = Layout;
    const { Text }        = Typography;
    const { RangePicker } = DatePicker;

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>휴가구분</div>,
                dataIndex: 'offDnm',
                key: 'offDnm',
                align: 'center',
                width: '10%',
                render: (text:string, record:IUserOffHisState) => <Button name='btn_showDetail' className='link-button' size='small' onClick={(e) => onClick(e, record)}>{text}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용자명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: '10%',
                render: (text:string, record:IUserOffHisState) => <Button name='btn_showDetail' className='link-button' size='small' onClick={(e) => onClick(e, record)}>{text}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>시작일자</div>,
                dataIndex: 'offStDt',
                key: 'offStDt',
                align: 'center',
                width: '10%',
                render: (text:string, record:IUserOffHisState) => <Button name='btn_showDetail' className='link-button' size='small' onClick={(e) => onClick(e, record)}>{commonDate.maskDate(text, 'YYYY-MM-DD')}</Button>
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>종료일자</div>,
                dataIndex: 'offEdDt',
                key: 'offEdDt',
                align: 'center',
                width: '10%',
                render: (text:string, record:IUserOffHisState) => <Button name='btn_showDetail' className='link-button' size='small' onClick={(e) => onClick(e, record)}>{commonDate.maskDate(text, 'YYYY-MM-DD')}</Button>
            }];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        const initInquiryCnds:IInquiryCndsState = {
            empNo : !common.isAdmin()  && !common.isDeptHdr() ? common.getEmpNo() : '',
            deptCd: !common.isAdmin()  && common.isEmptyString(inquiryCnds.deptCd) ? common.getDeptCd() : inquiryCnds.deptCd,

        }
        dispatch(Usr0101mAction.initState(initInquiryCnds));
        dispatch(Usr0101mAction.setCurrent(1));
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0101mAction.setUserOffHis([]));
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        if(Array.isArray(dateStrings)){
            const startDate = dateStrings[0].split('-').join('');
            const endDate   = dateStrings[1].split('-').join('');
            dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, offStDt : startDate, offEdDt : endDate }));
        }else{
            dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, baseYy : dateStrings }));
        }
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, ...record:IUserOffHisState[]) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_ResetResult();
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_showDetail': {
                const param = {
                    menuId : 'MUSR2021003',
                    props : {
                        apprNo : record[0].apprNo
                    }
                };
                common.openPage(param);
                break;
            }

            default:
        }
    };


    
    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
    const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A',
            deptCd: common.isAdmin() ? '' : common.getDeptCd()
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0101mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: any[] = [];

        if (common.isAdmin() || common.isDeptHdr()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            if (!common.isAdmin() && !common.isDeptHdr()) {
                if (item.empNo === common.getEmpNo()) {
                    arrUserOptions.push({
                        label: item.userNm,
                        value: item.empNo
                    });
                }
            } else {
                arrUserOptions.push({
                    label: item.userNm,
                    value: item.empNo
                });
            }
        });

        return arrUserOptions;
    }, [userList]);


    const setOffrDcdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IOffrDcdItemState[]) => {
                let arrOptions:IOffrDcdItemState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IOffrDcdItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });

                dispatch(Usr0101mAction.setOffrDcdList(arrOptions));
            }
        };
        common.getComCodeList('008', option);
    }

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        dispatch(Usr0101mAction.setCurrent(1));
        
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds,
            empNo: !common.isAdmin()  && !common.isDeptHdr() ? common.getEmpNo() : inquiryCnds.empNo,
            deptCd: !common.isAdmin() && common.isEmptyString(inquiryCnds.deptCd) ? common.getDeptCd() : inquiryCnds.deptCd,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0002',
            success : (responseData:any) => {
                dispatch(Usr0101mAction.setUserOffStatus(responseData.userOffStatus));
                dispatch(Usr0101mAction.setUserOffHis(responseData.userOffHis));
            },
        });
    };

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    return (
        <>
            <Layout>
                <Content>
                    <Row
                        align='middle'
                        justify='space-between'
                        style={{ marginTop: 10 }}
                    >
                        <Col>
                            <Space>
                                <Space direction='vertical' size='small'>
                                    <label style={{fontSize:'10px'}}>기준년도</label>
                                    <ConfigProvider locale={locale}>
                                        <DatePicker
                                            value={dayjs(inquiryCnds.baseYy)}
                                            onChange={calendar_onChange}
                                            style={{ width: 80 }}
                                            picker='year'/>
                                    </ConfigProvider>
                                </Space>
                                <Space direction='vertical' size='small'>
                                    <label style={{fontSize:'10px'}}>휴가구분</label>
                                    <Select
                                        style={{
                                            width: 140,
                                        }}
                                        size='small'
                                        defaultValue=''
                                        optionFilterProp='label'
                                        options={offrDcdList}
                                        onKeyUp={handleOnKeyUp}
                                        onChange={val => {
                                            dispatch(Usr0101mAction.setInquiryCnds({...inquiryCnds, offDcd: val}))
                                        }}
                                        value={inquiryCnds.offDcd}
                                    />
                                </Space>
                                <Space direction='vertical' size='small'>
                                    <label style={{fontSize:'10px'}}>직원명</label>
                                    <Select 
                                        size='small'
                                        showSearch
                                        style={{
                                            width: 110,
                                        }}
                                        defaultValue=''
                                        optionFilterProp='label'
                                        filterSort={(optionA: any, optionB: any) => {
                                            const labelA = (optionA?.label ?? '').toLowerCase();
                                            const labelB = (optionB?.label ?? '').toLowerCase();
                                        
                                            if (labelA === '전체') return -1;
                                            if (labelB === '전체') return 1;
                                        
                                            return labelA.localeCompare(labelB);
                                        }}
                                        options={userOptions()}
                                        onKeyUp={handleOnKeyUp}
                                        value={inquiryCnds.empNo}
                                        onChange={val => dispatch(Usr0101mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                    ></Select>
                                </Space>
                            </Space>
                        </Col>
                    </Row>
                    <Row>
                        <Col flex='9.5'>
                            <Space direction='vertical' size='small'>
                                <label style={{fontSize:'10px'}}>휴가일자</label>
                                <ConfigProvider locale={locale}>
                                    <RangePicker
                                        inputReadOnly
                                        style={{ width: 230 }}
                                        dropdownClassName='custom-vertical-picker'
                                        id='tx_calendar'
                                        allowClear={false}
                                        onChange={calendar_onChange}
                                        picker='date'
                                        value={[inquiryCnds.offStDt ? dayjs(inquiryCnds.offStDt) : null, inquiryCnds.offEdDt ? dayjs(inquiryCnds.offEdDt) : null]}

                                    />
                                </ConfigProvider>
                            </Space>
                        </Col>
                        <Col flex='0.5' style={{marginTop:'32px'}}>
                            <Button
                                name='btn_Search'
                                size='small'
                                className='background-color-primary-button'
                                icon={<SearchOutlined />}
                                onClick={onClick}
                            />
                        </Col>
                    </Row>
                    <Row align='middle' style={{ height: 50, width:'100%' }}>
                        <Space>
                            <Col>
                                <Descriptions bordered column={3} size='small'>
                                    <Descriptions.Item style={{fontSize:'10px'}} label='부여'>{userOffStatus.giveOffDays + userOffStatus.addOffDays}</Descriptions.Item>
                                    <Descriptions.Item style={{fontSize:'10px'}} label='사용'>{userOffStatus.useOffDays}</Descriptions.Item>
                                    <Descriptions.Item style={{fontSize:'10px'}} label='잔여'>{userOffStatus.remainOffDays}</Descriptions.Item>
                                </Descriptions>
                            </Col>
                        </Space>
                    </Row>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>휴가이력</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Row>
                        <Table
                            dataSource={userOffHis}
                            key='no'
                            columns={columns()}
                            size='small'
                            style={{width:'100%'}}
                            scroll={{y:340}}
                            pagination={false}
                        />
                    </Row>
                </Content>
            </Layout>
        </>
    );
};

export default Usr0101m;

